import { Component, TemplateFunction } from '@ribajs/core'
import { SbLocaleService } from '../services/sb-locale.service'

import type { ShopifySellingPlan } from '../types'

interface Scope {
  // PROPERTIES
  availableSellingPlans: ShopifySellingPlan[]
  selectedSellingPlan: ShopifySellingPlan | null
  isSubscriptionSelected: boolean

  // METHODS
  toggleSubscription: SbSubscriptionSelectorComponent['toggleSubscription']
  enableSubscription: SbSubscriptionSelectorComponent['enableSubscription']
  disableSubscription: SbSubscriptionSelectorComponent['disableSubscription']
  selectSellingPlan: SbSubscriptionSelectorComponent['selectSellingPlan']
  getDiscountLabel: SbSubscriptionSelectorComponent['getDiscountLabel']
}

/**
 * @see https://shopify.dev/docs/storefronts/themes/pricing-payments/subscriptions/add-subscriptions-to-your-theme
 */
export class SbSubscriptionSelectorComponent extends Component {
  public static tagName = 'sb-subscription-selector'

  protected localeService = SbLocaleService.getSingleton()

  public scope: Scope = {
    availableSellingPlans: [],
    selectedSellingPlan: null,
    isSubscriptionSelected: false,
    toggleSubscription: this.toggleSubscription.bind(this),
    enableSubscription: this.enableSubscription.bind(this),
    disableSubscription: this.disableSubscription.bind(this),
    selectSellingPlan: this.selectSellingPlan.bind(this),
    getDiscountLabel: this.getDiscountLabel.bind(this),
  }

  static get observedAttributes(): string[] {
    return ['available-selling-plans']
  }

  protected parsedAttributeChangedCallback(
    attributeName: string,
    oldValue: any,
    newValue: any,
    namespace: string | null,
  ): void {
    super.parsedAttributeChangedCallback(
      attributeName,
      oldValue,
      newValue,
      namespace,
    )
    switch (attributeName) {
      case 'availableSellingPlans':
        if (newValue !== oldValue) {
          this.onSellingPlansChanged()
        }
        break
    }
  }

  protected async onSellingPlansChanged() {
    // Reset subscription selection
    this.scope.selectedSellingPlan = null
    this.scope.isSubscriptionSelected = false
  }

  public toggleSubscription(event?: Event) {
    event?.preventDefault()

    this.scope.isSubscriptionSelected = !this.scope.isSubscriptionSelected

    if (this.scope.isSubscriptionSelected) {
      return this.enableSubscription(event)
    }

    return this.disableSubscription(event)
  }

  public enableSubscription(event?: Event) {
    event?.preventDefault()

    this.scope.isSubscriptionSelected = true

    if (!this.scope.selectedSellingPlan) {
      // Select first plan by default
      const firstPlan = this.scope.availableSellingPlans[0]
      if (firstPlan) {
        this.selectSellingPlan(firstPlan)
      }
    }
  }

  public disableSubscription(event?: Event) {
    event?.preventDefault()

    this.scope.isSubscriptionSelected = false
    this.selectSellingPlan(null)
  }

  public selectSellingPlan(plan: ShopifySellingPlan | null) {
    this.scope.selectedSellingPlan = plan

    this.dispatchEvent(
      new CustomEvent('subscription:changed', {
        detail: {
          sellingPlan: plan,
        },
        bubbles: true,
      }),
    )
  }

  public getDiscountLabel(plan: ShopifySellingPlan) {
    const priceAdjustment = plan.price_adjustments[0]
    if (!priceAdjustment) {
      return ''
    }

    let value = ''
    if (priceAdjustment.value_type === 'percentage') {
      value = priceAdjustment.value + '%'
    }

    return this.localeService._('subscription.discount_html', {
      value,
    })
  }

  protected template(): ReturnType<TemplateFunction> {
    return null // Template will be defined in Liquid
  }
}
